import React from "react";
import { useQueries } from "react-query";
import { useUserState } from "./UserContext";

import {
  getProductValues,
  getNetworks,
  getGateways,
  getCountryCodes,
  getCurrencyCodes,
  getTaxRegions,
  getLanguages,
  getTimezones,
  getContactTypes,
  getAuthSignatureMethods,
  getSMPPEncodings,
  getAPIChannels,
  getCredentialTypes,
  getEnterpriseAccountTypes,
  getSmsConfig,
  getTwofaPricingUnits,
  getEnvironment,
} from "./staticData";

const StaticDataStateContext = React.createContext();

const requiredStaticDataKeys = [
  "environment",
  "gateways",
  "taxRegions",
  "languages",
  "products",
  "twofaPricingUnits",
  "countryCodes",
  "networks",
  "authSignatureMethods",
  "smppEncodings",
  "apiChannels",
  "currencyCodes",
  "credentialTypes",
  "enterpriseAccountTypes",
  "contactTypes",
  "timezones",
  "smsConfig",
];

const requiredPublicStaticDataKeys = ["environment", "timezones", "contactTypes"];

//this hook fetches all of the static data for the application at once
const useGetStaticData = (staticDataKeys) => {
  const queries = [];

  staticDataKeys.forEach((staticDataKey) => {
    let queryFn;
    switch (staticDataKey) {
      case "environment": {
        queryFn = getEnvironment;
        break;
      }
      case "products": {
        queryFn = getProductValues;
        break;
      }
      case "twofaPricingUnits": {
        queryFn = getTwofaPricingUnits;
        break;
      }
      case "networks": {
        queryFn = getNetworks;
        break;
      }
      case "gateways": {
        queryFn = getGateways;
        break;
      }
      case "countryCodes": {
        queryFn = getCountryCodes;
        break;
      }
      case "currencyCodes": {
        queryFn = getCurrencyCodes;
        break;
      }
      case "taxRegions": {
        queryFn = getTaxRegions;
        break;
      }
      case "languages": {
        queryFn = getLanguages;
        break;
      }
      case "timezones": {
        queryFn = getTimezones;
        break;
      }
      case "contactTypes": {
        queryFn = getContactTypes;
        break;
      }
      case "authSignatureMethods": {
        queryFn = getAuthSignatureMethods;
        break;
      }
      case "smppEncodings": {
        queryFn = getSMPPEncodings;
        break;
      }
      case "apiChannels": {
        queryFn = getAPIChannels;
        break;
      }
      case "credentialTypes": {
        queryFn = getCredentialTypes;
        break;
      }
      case "enterpriseAccountTypes": {
        queryFn = getEnterpriseAccountTypes;
        break;
      }
      case "smsConfig": {
        queryFn = getSmsConfig;
        break;
      }
      default:
        break;
    }

    queries.push({
      queryKey: [staticDataKey],
      queryFn: () => queryFn(),
    });
  });

  const staticData = useQueries(queries);
  const results = {};
  staticData.forEach((currentItem, index) => {
    results[staticDataKeys[index]] = currentItem.data || [];
  });

  if (Object.entries(results).length !== 0) return results;
};

const StaticDataProvider = ({ children }) => {
  const { isAuthenticated } = useUserState();

  const initialState =
    useGetStaticData(
      isAuthenticated ? requiredStaticDataKeys : requiredPublicStaticDataKeys,
    ) || {};

  return (
    <StaticDataStateContext.Provider value={initialState}>
      {children}
    </StaticDataStateContext.Provider>
  );
};

const useStaticData = () => {
  const context = React.useContext(StaticDataStateContext);

  if (context === undefined) {
    throw new Error("StaticDataState must be used within a StaticDataProvider");
  }
  return context;
};

export { StaticDataProvider, useStaticData };
